/**
 * Configuración centralizada de URLs de APIs
 *
 * Variables de entorno (OBLIGATORIAS en producción - deben estar disponibles en BUILD TIME):
 *  - REACT_APP_API_URL          -> URL completa de calc-api (ej: https://api.trimergy.io)
 *  - REACT_APP_MILP_API_URL     -> URL completa de milp-api (ej: https://milp-api.trimergy.io)
 *
 * Fallbacks:
 *  - Desarrollo: localhost con puertos específicos
 *  - Producción: REQUIERE que las variables de entorno estén configuradas con HTTPS
 *
 * Nota: REACT_APP_ECOTRIM_API_URL está deprecada. Los endpoints económicos/plots BESS
 * viven en milp-api; el histórico de trafos vive en calc-api (USER_API).
 */

// Helper para normalizar URLs (agregar https:// si falta; quitar / final)
const normalizeUrl = (url) => {
  if (!url) return url;
  let normalized = url;
  // Si no tiene protocolo, agregar https://
  if (!normalized.startsWith('http://') && !normalized.startsWith('https://')) {
    normalized = `https://${normalized}`;
  }
  // Evitar //api/... cuando se concatena `${BASE}/api/...`
  return normalized.replace(/\/+$/, '');
};

// Detectar si estamos en desarrollo
const isDevelopment = process.env.NODE_ENV === 'development' || 
                      (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'));

// URLs base para cada API
// En desarrollo: usa localhost directamente
// En producción: usa las variables de entorno con HTTPS (normalizadas si falta el protocolo)
// Si las variables no están configuradas, muestra error en consola
// REACT_APP_DEV_API_URL (solo desarrollo): apunta a un calc-api en otro puerto,
// p. ej. el de un worktree en 4001 mientras el checkout principal ocupa el 4000.
const USER_API_BASE_URL = isDevelopment 
  ? normalizeUrl(process.env.REACT_APP_DEV_API_URL || 'http://localhost:4000')
  : (process.env.REACT_APP_API_URL 
      ? normalizeUrl(process.env.REACT_APP_API_URL)
      : (console.error('❌ REACT_APP_API_URL no está configurada. Configúrala en GitHub Variables.'), '/api/backend'));
  
const MILP_API_BASE_URL = isDevelopment
  ? normalizeUrl(process.env.REACT_APP_MILP_API_URL || 'http://localhost:8000')
  : (process.env.REACT_APP_MILP_API_URL
      ? normalizeUrl(process.env.REACT_APP_MILP_API_URL)
      : (console.error('❌ REACT_APP_MILP_API_URL no está configurada. Configúrala en GitHub Variables.'), '/api/milp'));

// URLs específicas de cada API
export const API_CONFIG = {
  // API MILP (optimización, modelo económico y plots BESS)
  MILP_API: {
    BASE_URL: MILP_API_BASE_URL,
    ENDPOINTS: {
      CUSTOM_OPTIMIZATION: '/custom_optimization',
      OPTIMIZE_BESS: '/optimize-bess',
      OPTIMIZE_BESS_ARBITRAGE: '/optimize-bess-arbitrage',
      OPTIMIZE_BESS_COOPTIMIZATION: '/optimize-bess-cooptimization',
      SIZE_BESS: '/size-bess',
      ECONOMIC_MODEL_FROM_OPTIMIZATION: '/economic_model_from_optimization',
      BESS_STATISTICS: '/api/metrics/bess-statistics',
      BESS_SOC: '/api/plots/bess-soc',
      BESS_FLOWS: '/api/plots/bess-flows',
      BESS_ENERGY_BALANCE: '/api/plots/bess-energy-balance',
      BESS_HOURLY_OPERATION: '/api/plots/bess-hourly-operation',
    }
  },

  // Deprecated: ecotrim-api (R) retirado. Endpoints económicos/plots → MILP_API;
  // histórico de trafos → USER_API. BASE_URL redirige a milp por compatibilidad.
  ECOTRIM_API: {
    BASE_URL: MILP_API_BASE_URL,
    ENDPOINTS: {},
  },

  // API Usuario (autenticación y gestión) - Backend Node.js
  USER_API: {
    BASE_URL: USER_API_BASE_URL,
    ENDPOINTS: {
      REGISTER: '/api/auth/register',
      LOGIN: '/api/auth/login',
      GOOGLE: '/api/auth/google',
      PROFILE: '/api/auth/profile',
      UPDATE_PROFILE: '/api/auth/profile',
      CHANGE_PASSWORD: '/api/auth/change-password',
      LOGOUT: '/api/auth/logout',
      PENDING_SIGNUP: '/api/auth/pending-signup',
      VERIFY_TOKEN: '/api/auth/verify-token',
      REFRESH_TOKEN: '/api/auth/refresh-token',
      HEALTH: '/health',
      MESSAGE: '/api/auth/message',
      CONTACT: '/api/contact',
      BATTERIES: '/api/batteries'
    }
  },
  
  // CDNs externos
  EXTERNAL: {
    PLOTLY_CDN: 'https://cdn.plot.ly/plotly-2.35.2.min.js', // Updated to specific version
  }
};

// Funciones helper para construir URLs completas
export const buildApiUrl = (apiName, endpoint, params = {}) => {
  const config = API_CONFIG[apiName];
  if (!config) {
    throw new Error(`API configuration not found for: ${apiName}`);
  }
  
  const baseUrl = config.BASE_URL;
  const endpointPath = config.ENDPOINTS[endpoint];

  if (!endpointPath) {
    throw new Error(`Endpoint not found: ${endpoint} for API: ${apiName}`);
  }
  
  let url = `${baseUrl}${endpointPath}`;
  
  // Agregar parámetros de consulta si existen
  if (Object.keys(params).length > 0) {
    const queryString = new URLSearchParams(params).toString();
    url += `?${queryString}`;
  }

  return url;
};

// Funciones específicas para cada API
export const getMilpApiUrl = (endpoint, params = {}) => {
  return buildApiUrl('MILP_API', endpoint, params);
};

/**
 * @deprecated ecotrim-api (R) retirado. Redirige a MILP_API para compatibilidad temporal.
 */
export const getEcotrimApiUrl = (endpoint, params = {}) => {
  return buildApiUrl('MILP_API', endpoint, params);
};

// Función de compatibilidad (mantener por si hay código que la usa)
export const getUserApiUrl = (endpoint, params = {}) => {
  return buildApiUrl('USER_API', endpoint, params);
};
export const getMessageApiUrl = (endpoint, params = {}) => {
  return buildApiUrl('USER_API', endpoint, params);
};

// Feature Flags
export const FEATURE_FLAGS = {
  ASYNC_OPTIMIZATION: process.env.REACT_APP_ASYNC_OPTIMIZATION === 'true',
  // Prueba gratuita de 7 días desactivada temporalmente; debe ir alineado con SUBSCRIPTION_TRIAL_ENABLED de calc-api.
  TRIAL_ENABLED: process.env.REACT_APP_TRIAL_ENABLED === 'true',
};

export default API_CONFIG;
